import { useState } from 'react';

import RefreshIcon from '@mui/icons-material/Refresh';
import PropTypes from 'prop-types';

import Button from '@zenbusiness/zui/Button';

import { CLICKSTREAM_COMMON_PROPS } from '#client/components/utils/constants';

import NameCard from './NameCard';
import * as Styled from '../../BusinessNameResults.style';

/**
 * Left sidebar panel containing list of items
 * @param {object} props - Component props
 * @param {Array} props.items - Array of item suggestions
 * @param {string} props.selectedItem - Currently selected item
 * @param {Function} props.onItemSelect - Handler for item selection
 * @param {Function} [props.onGenerateMore] - Handler for generating more items (optional, hides button if not provided)
 * @param {Function} [props.onStartOver] - Handler for starting over (optional, hides button if not provided)
 * @param {boolean} [props.loading=false] - Whether more items are being generated
 * @component
 */
function NamesPanel({
  items,
  selectedItem,
  onItemSelect,
  onGenerateMore,
  onStartOver,
  loading = false
}) {
  const [generateLoading, setGenerateLoading] = useState(false);
  const [startOverLoading, setStartOverLoading] = useState(false);

  const showGenerateMore = Boolean(onGenerateMore);
  const showStartOver = Boolean(onStartOver);
  const showButtons = showGenerateMore || showStartOver;

  const handleGenerateMore = async () => {
    if (generateLoading || startOverLoading || loading || !onGenerateMore) return;

    setGenerateLoading(true);
    await onGenerateMore();
    setGenerateLoading(false);
  };

  const handleStartOver = async () => {
    if (startOverLoading || generateLoading || loading || !onStartOver) return;

    setStartOverLoading(true);
    await onStartOver();
    setStartOverLoading(false);
  };

  return (
    <Styled.NamesSidebar>
      <Styled.NamesList>
        {items.map((item) => {
          // Support both 'name' and 'title' keys for flexibility
          const displayTitle = item.name || item.title;
          return (
            <NameCard
              key={displayTitle}
              item={item}
              selected={displayTitle === selectedItem}
              onClick={onItemSelect}
            />
          );
        })}
      </Styled.NamesList>

      {showButtons && (
        <Styled.ButtonsContainer>
          {showGenerateMore && (
            <Button
              clickstreamProps={{
                ...CLICKSTREAM_COMMON_PROPS,
                elementName: 'business_name_generate_more',
                elementText: 'Generate more names'
              }}
              color="info"
              disabled={generateLoading || startOverLoading || loading}
              fullWidth
              loading={generateLoading}
              size="medium"
              startIcon={<RefreshIcon />}
              variant="outlined"
              onClick={handleGenerateMore}
            >
              Generate more names
            </Button>
          )}
          {showStartOver && (
            <Button
              clickstreamProps={{
                ...CLICKSTREAM_COMMON_PROPS,
                elementName: 'business_name_start_over',
                elementText: 'Start over'
              }}
              disabled={generateLoading || startOverLoading || loading}
              fullWidth
              loading={startOverLoading}
              size="medium"
              variant="outlined"
              onClick={handleStartOver}
            >
              Start over
            </Button>
          )}
        </Styled.ButtonsContainer>
      )}
    </Styled.NamesSidebar>
  );
}

NamesPanel.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      title: PropTypes.string,
      reason: PropTypes.string
    })
  ).isRequired,
  selectedItem: PropTypes.string,
  onItemSelect: PropTypes.func,
  onGenerateMore: PropTypes.func,
  onStartOver: PropTypes.func,
  loading: PropTypes.bool
};

export default NamesPanel;
